/**
 * WHEN to start `peterhof_live`'s report run — the plain-object decision
 * behind `App.tsx`'s `scheduleReport`, kept as a pure module so it is
 * testable with no React, no store and no network. `App.tsx` owns the state
 * object and calls the three functions below at the three moments that can
 * change the answer: a `worker_done` frame arrives, the visitor's own turn
 * ends, and the visitor sends a message (which cancels a deferred report,
 * §plan «Архитектура» — the backend folds that job's brief into the turn they
 * just sent instead, `on_turn_start`'s `pending_reports`).
 *
 * Three states one delegation's report can be in, tracked by which of the two
 * sets its id sits in: **nothing yet** (in neither — `worker_done` has not
 * arrived, or it arrived and was cancelled by a visitor message before it
 * could start); **PENDING** (`worker_done` arrived while the chat was busy —
 * waiting for `onEndTurn` to fire it, or for a visitor message to cancel it
 * first); **STARTED** (a report run has actually begun — guards against a
 * second `worker_done` for the same id, e.g. a replayed worker stream after
 * an F5, starting a second run).
 */

export type ReportSchedulerState = {
  /** delegationIds whose `worker_done` arrived while the chat was busy —
   *  waiting for `onEndTurn`. */
  pending: Set<string>;
  /** delegationIds whose report run has already been started. */
  started: Set<string>;
};

export function createReportSchedulerState(): ReportSchedulerState {
  return { pending: new Set(), started: new Set() };
}

export type SchedulerDecision =
  | { action: 'start' }
  | { action: 'defer' }
  | { action: 'ignore' };

/**
 * `worker_done` arrived for `delegationId`. `chatIsIdle`/`composerIsEmpty` are
 * read by the caller off `useChat`'s own state at the moment the frame lands
 * (`status === 'idle'`, the composer draft trimmed to nothing) — both must
 * hold, or a report bubble would interrupt a turn the visitor is mid-typing.
 *
 * Idempotent by construction: a `delegationId` already in `started` (or still
 * sitting in `pending`, waiting on an earlier `worker_done`) is ignored on a
 * repeat — the wire contract allows the frame twice (replay after F5), and
 * starting the run a second time would draw the same bubble twice.
 */
export function onWorkerDone(
  state: ReportSchedulerState,
  delegationId: string,
  chatIsIdle: boolean,
  composerIsEmpty: boolean,
): SchedulerDecision {
  if (state.started.has(delegationId) || state.pending.has(delegationId)) return { action: 'ignore' };
  if (chatIsIdle && composerIsEmpty) {
    state.started.add(delegationId);
    return { action: 'start' };
  }
  state.pending.add(delegationId);
  return { action: 'defer' };
}

/**
 * The visitor's own turn just ended (`useChat.endTurn()`). Every deferred
 * report may now start — UNLESS the visitor is already typing again
 * (`composerIsEmpty` false), in which case they stay pending for the NEXT
 * `endTurn`. Returns the ids to actually start, moving each from `pending`
 * into `started` so a later call cannot start it twice.
 */
export function onEndTurn(state: ReportSchedulerState, composerIsEmpty: boolean): string[] {
  if (state.pending.size === 0 || !composerIsEmpty) return [];
  const ids = [...state.pending];
  state.pending.clear();
  for (const id of ids) state.started.add(id);
  return ids;
}

/**
 * The visitor sent a message — any report still waiting in `pending` is
 * dropped rather than started later: the backend folds that job's brief into
 * the turn the visitor just sent (`on_turn_start`'s `pending_reports` →
 * `user_extra`), so a separate report run for it would retell the same
 * result twice. Deliberately does NOT touch `started` — a report already
 * running is not cancelled by a new message arriving beside it.
 */
export function cancelDeferred(state: ReportSchedulerState): void {
  state.pending.clear();
}

/** «Новый диалог» — a fresh thread has no jobs of its own. */
export function resetReportScheduler(state: ReportSchedulerState): void {
  state.pending.clear();
  state.started.clear();
}
